import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, AlertCircle, CheckCircle, AlertTriangle, XCircle, Clock } from 'lucide-react'
import { systemHealthApi } from '../../lib/api'
import { formatDatetime } from '../../lib/utils'
import type { SystemHealthCard } from '../../types'

import { tx } from '../../lib/i18n'
const statusConfig = {
  healthy: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  degraded: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500 animate-pulse' },
  down: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500 animate-pulse' },
}

const latencyColor = (ms: number | null): string => {
  if (ms === null) return 'text-gray-400'
  if (ms < 100) return 'text-green-600'
  if (ms < 500) return 'text-amber-600'
  return 'text-red-600'
}

const SystemHealthPage: React.FC = () => {
  const [lastRefresh, setLastRefresh] = useState(new Date())

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => systemHealthApi.getStatus().then((r) => r.data.data),
    refetchInterval: 30000,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      refetch()
      setLastRefresh(new Date())
    }, 30000)
    return () => clearInterval(interval)
  }, [refetch])

  const services = data?.services ?? []
  const errors = data?.errors ?? []

  const healthyCount = services.filter((s) => s.status === 'healthy').length
  const degradedCount = services.filter((s) => s.status === 'degraded').length
  const downCount = services.filter((s) => s.status === 'down').length

  const overallStatus = downCount > 0 ? 'down' : degradedCount > 0 ? 'degraded' : 'healthy'
  const overallConfig = statusConfig[overallStatus]
  const OverallIcon = overallConfig.icon

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('System Health')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{tx('Real-time status of all platform services')}</p>
        </div>
        <button
          onClick={() => { refetch(); setLastRefresh(new Date()) }}
          disabled={isLoading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          {tx('Refresh')}
        </button>
      </div>

      {/* Overall status banner */}
      <div className={`flex items-center gap-4 p-4 rounded-xl border ${overallConfig.bg}`}>
        <OverallIcon className={`h-6 w-6 ${overallConfig.color} flex-shrink-0`} />
        <div className="flex-1">
          <p className={`text-sm font-semibold ${overallConfig.color}`}>
            {overallStatus === 'healthy' && 'All Systems Operational'}
            {overallStatus === 'degraded' && 'Some Systems Degraded'}
            {overallStatus === 'down' && 'Critical Issues Detected'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {healthyCount} healthy · {degradedCount} degraded · {downCount} down
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Clock className="h-3.5 w-3.5" />
          {tx('Auto-refreshes every 30s · Last:')} {lastRefresh.toLocaleTimeString()}
        </div>
      </div>

      {/* Service cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {services.map((svc) => {
          const config = statusConfig[svc.status]
          const Icon = config.icon
          return (
            <div key={svc.service} className={`bg-white rounded-xl border shadow-sm p-5 ${svc.status !== 'healthy' ? `border-${svc.status === 'degraded' ? 'amber' : 'red'}-200` : 'border-gray-200'}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${config.dot}`} />
                  <p className="text-sm font-semibold text-gray-800">{svc.service}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${config.badge}`}>
                  {svc.status}
                </span>
              </div>
              {svc.latencyMs !== null && (
                <div className="flex items-center gap-1.5 mb-2">
                  <p className="text-xs text-gray-400">{tx('Latency')}</p>
                  <p className={`text-sm font-bold font-mono ${latencyColor(svc.latencyMs)}`}>{svc.latencyMs}ms</p>
                </div>
              )}
              {svc.message && (
                <p className={`text-xs mt-2 ${svc.status === 'degraded' ? 'text-amber-600' : svc.status === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
                  {svc.message}
                </p>
              )}
              <p className="text-xs text-gray-300 mt-2">
                {tx('Checked:')} {formatDatetime(svc.lastChecked)}
              </p>
            </div>
          )
        })}
      </div>

      {/* Recent errors */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <h2 className="text-sm font-semibold text-gray-800">{tx('Recent Errors & Warnings')}</h2>
          <span className="ml-auto text-xs text-gray-400">{errors.length} events</span>
        </div>
        {errors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10">
            <CheckCircle className="h-8 w-8 text-green-400 mb-2" />
            <p className="text-sm text-gray-400">{tx('No recent errors. All clear!')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {errors.map((err, i) => (
              <div key={i} className="flex items-start gap-3 px-5 py-3">
                <span className={`mt-0.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${err.level === 'error' ? 'bg-red-500' : 'bg-amber-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">{err.service}</span>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${err.level === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{err.level}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5 truncate">{err.message}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{formatDatetime(err.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SystemHealthPage
